import { ExternalLinkIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useUserContext } from '@/Context/UserContext';
import UIStrings from '@/Utils/UIStrings';
import { DialogEnum, useActionDialogContext } from '../../Context/ActionDialogContext';
import { useCompanyContext } from '@/Context/CompanyContext';
import { useAccountSettings } from '@/api/useAccountSettings';
import { getEmailTemplateHtml } from '@/Pages/AccountSettings/utils/getEmailTemplateHtml';
import { Button, Spinner } from '@/Components/shadcn';

function InviteForInterviewActions(): React.JSX.Element {
    const { data: accountSettings } = useAccountSettings();

    const { title, thankYouMessage, bodyHtml, regards } = accountSettings.inviteEmailTemplate;

    const [body, setBody] = useState('');
    const { user } = useUserContext();
    const { currentCompanyProfile } = useCompanyContext();
    const {
        selectedCandidates,
        showInviteForInterviewNotification,
        hideDialog,
        showDialog,
        inviteForInterviewApplicantAsync,
        isInviteForInterviewLoading: isInterviewLoading,
    } = useActionDialogContext();
    const candidate = selectedCandidates[0];

    /** for "mailto:" link protocol (can't support html [except thunderbird clients with html-body instead of body]) */
    const parseLexicalHtmlTemplateToPlainText = (lexicalHtmlStr: string) => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(lexicalHtmlStr, 'text/html');
        let plainText = '';
        dom.body.childNodes.forEach((node) => {
            // whitespace between br
            if (node.nodeName === '#text') {
                return;
            }

            // list
            if (node.nodeName === 'OL' || node.nodeName === 'UL') {
                node.childNodes.forEach((listItemNode) => {
                    plainText += `${listItemNode.textContent}\n`;
                });

                return;
            }

            plainText += `${node.textContent}\n`;
        });

        return plainText.trimEnd();
    };

    useEffect(() => {
        setBody(
            parseLexicalHtmlTemplateToPlainText(
                getEmailTemplateHtml(
                    title,
                    thankYouMessage,
                    bodyHtml,
                    regards,
                    candidate.name,
                    `${user.firstName} ${user.lastName}`,
                    currentCompanyProfile.companyName,
                ),
            ),
        );
    }, [candidate]);

    const closeDialog = () => {
        hideDialog(DialogEnum.InviteForInterview);
    };

    const subject = `${currentCompanyProfile.companyName} - ${UIStrings.InvitationForInterview}`;

    let mailtoUrl = `mailto:${candidate.candidateEmail}`;
    const params = new URLSearchParams();
    if (subject) params.set('subject', subject);
    if (body) params.set('body', body);
    const queryString = params.toString();
    if (queryString) mailtoUrl += `?${queryString.replace(/\+/g, '%20')}`;

    return (
        <div className="flex flex-row gap-2.5">
            <Button
                asChild
                className="w-1/2"
                variant="outline"
                size="lg"
                onClick={() => {
                    closeDialog();
                    showDialog([candidate], DialogEnum.InviteForInterviewConfirm);
                }}
            >
                <a href={mailtoUrl} target="_blank" rel="noreferrer">
                    {UIStrings.ComposeInvitationEmail}
                    <ExternalLinkIcon data-icon="inline-end" />
                </a>
            </Button>
            <Button
                className="w-1/2"
                size="lg"
                variant="default"
                disabled={isInterviewLoading}
                onClick={async () => {
                    await inviteForInterviewApplicantAsync();
                    closeDialog();
                    showInviteForInterviewNotification();
                }}
            >
                {isInterviewLoading && <Spinner data-icon="inline-start" />}
                {UIStrings.MarkAsInviteForInterview}
            </Button>
        </div>
    );
}

export default InviteForInterviewActions;
